<template>
  <UiModal
    v-model="isOpen"
    :title="modalTitle"
    size="xlarge"
    :show-footer="true"
    :no-dim="true"
    :body-scroll-lock="false"
    class="step07-capa-item-picker-modal"
  >
    <div v-if="loading" class="step07-capa-item-picker-modal__state step07-capa-item-picker-modal__state--sized">{{ st('m_a985a9f1cc') }}</div>
    <div v-else-if="!hasContent" class="step07-capa-item-picker-modal__state step07-capa-item-picker-modal__state--sized">{{ t('hrEvaluation.conf.targets.noCapaItems') }}</div>
    <div v-else class="step07-capa-item-picker-modal__layer">
      <!-- hreConfMngList.jsp #capaItemPopAll / openCapaItemAll — 역량사전(cmptDict)별 그룹·체크 -->
      <div class="step07-capa-item-picker-modal__activity">
        <div v-for="sec in dictSections" :key="sec.cmptDictId" class="step07-capa-item-picker-modal__dict">
          <template v-if="sec.aCnt > 0">
            <div class="step07-capa-item-picker-modal__dict-head">
              <button
                type="button"
                class="step07-capa-item-picker-modal__expand"
                :class="isDictExpanded(sec.cmptDictId) ? 'is-open' : 'is-closed'"
                :title="isDictExpanded(sec.cmptDictId) ? st('targets.collapse') : st('targets.expand')"
                :aria-label="st('m_d5e57ac56c')"
                @click="toggleExpand(sec.cmptDictId)"
              />
              <UiCheckbox
                size="large"
                :model-value="sec.allChecked"
                :label="t('hrEvaluation.conf.targets.selectAll')"
                class="step07-capa-item-picker-modal__dict-master"
                @update:model-value="v => toggleAllInDict(sec.cmptDictId, v)"
              />
              <strong class="step07-capa-item-picker-modal__dict-nm">{{ sec.cmptDictNm }}</strong>
            </div>
            <div
              v-show="isDictExpanded(sec.cmptDictId)"
              class="step07-capa-item-picker-modal__dict-body"
            >
              <table class="step07-capa-item-picker-modal__tbl">
                <caption class="sr-only">{{ sec.cmptDictNm }}</caption>
                <tbody>
                  <tr v-for="it in sec.items" :key="`${it.cmptDictId}_${it.capaItemId}`">
                    <td class="step07-capa-item-picker-modal__cell">
                      <UiCheckbox
                        size="large"
                        :model-value="it.checked"
                        :label="itemLabel(it)"
                        @update:model-value="v => toggleOneItem(it, v)"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </div>
    </div>
    <template #footerActions>
      <UiButton variant="secondary" @click="close">{{ st('m_218e2ae6c9') }}</UiButton>
      <UiButton variant="primary" :disabled="loading" @click="onConfirm">{{ t('hrEvaluation.conf.basis.common.action.save') }}</UiButton>
    </template>
  </UiModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import UiModal from '~/components/common/UI/UiModal.vue';
import UiButton from '~/components/common/UI/UiButton.vue';
import UiCheckbox from '~/components/common/UI/UiCheckbox.vue';
import { useSettingsApi } from '~/composables/hr-evaluation/conf/settings/useSettingsApi';
import { useHrEvalSettingsI18n } from '~/composables/hr-evaluation/conf/settings/useHrEvalSettingsI18n';
const { st, t } = useHrEvalSettingsI18n();

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  findYear: { type: String, default: '' },
  evalCycleCd: { type: String, default: '' },
  evalGrpId: { type: String, default: '' },
  capaGbnRow: { type: Object, default: null },
  cmptGrpId: { type: String, default: '' },
  preselectedItems: { type: Array, default: () => [] },
  fallbackItems: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue', 'confirm']);

const { fetchCapaItemAllList } = useSettingsApi();

const isOpen = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v),
});

const loading = ref(false);
/** @type {import('vue').Ref<Array<{ capaItemId: string; capaItemNm: string; cmptDictId: string; weight: string; checked: boolean }>>} */
const capaItemRows = ref([]);
const cmptDictListRaw = ref([]);
/** 역량사전 블록 펼침 (JSP: pCnt==0 이면 접힌 상태로 시작) */
const expandedByDict = ref({});

const capaGbnId = computed(() => String(props.capaGbnRow?.capaGbnId ?? ''));

const weightUseYn = computed(() => String(props.capaGbnRow?.weightUseYn ?? '') === 'Y');

const modalTitle = computed(() => {
  const nm = props.capaGbnRow?.capaGbnNm || props.capaGbnRow?.capaGbnId || '';
  return nm ? String(nm) : t('hrEvaluation.conf.targets.capaItemDefaultTitle');
});

const dictSections = computed(() => {
  const headers = cmptDictListRaw.value;
  return headers.map(h => {
    const did = String(h.cmptDictId ?? '');
    const items = capaItemRows.value.filter(i => i.cmptDictId === did);
    const aCnt = items.length;
    const pCnt = items.filter(i => i.checked).length;
    return {
      cmptDictId: did,
      cmptDictNm: String(h.cmptDictNm ?? ''),
      aCnt,
      pCnt,
      allChecked: aCnt > 0 && pCnt === aCnt,
      items,
    };
  });
});

const hasContent = computed(() => dictSections.value.some(s => s.aCnt > 0));

const itemLabel = it => {
  const nm = it.capaItemNm || it.capaItemId;
  if (weightUseYn.value && it.weight) return `${nm} ( ${it.weight}% )`;
  return nm;
};

const isDictExpanded = cmptDictId => {
  const v = expandedByDict.value[cmptDictId];
  if (v !== undefined) return v;
  return true;
};

const toggleExpand = cmptDictId => {
  const cur = isDictExpanded(cmptDictId);
  expandedByDict.value = { ...expandedByDict.value, [cmptDictId]: !cur };
};

/**
 * JSP openCapaItemAll: 기존 configAll.capa.itemList 와 매칭해 checked / pCnt 반영
 */
/**
 * cmptDictId가 있으면 capaItemId|cmptDictId 쌍으로 매칭.
 * 서버 capaItemList는 cmptDictId 없이 capaItemId만 내려오는 경우가 있어, 없을 때는 id만으로 매칭한다.
 */
const applyPreselected = (rows, preList) => {
  const pairSet = new Set();
  const idOnlySet = new Set();
  for (const p of preList || []) {
    const id = String(p.capaItemId ?? '');
    const did = String(p.cmptDictId ?? '').trim();
    if (!id) continue;
    if (did) pairSet.add(`${id}|${did}`);
    else idOnlySet.add(id);
  }
  const idsWithPair = new Set([...pairSet].map(k => k.split('|')[0]));
  return rows.map(r => {
    const k = `${r.capaItemId}|${r.cmptDictId}`;
    const checked = pairSet.has(k) || (idOnlySet.has(r.capaItemId) && !idsWithPair.has(r.capaItemId));
    return { ...r, checked };
  });
};

const initExpandedFromRows = rows => {
  const next = {};
  for (const d of cmptDictListRaw.value) {
    const did = String(d.cmptDictId ?? '');
    const items = rows.filter(r => r.cmptDictId === did);
    const aCnt = items.length;
    const pCnt = items.filter(r => r.checked).length;
    next[did] = aCnt > 0 && pCnt > 0;
  }
  expandedByDict.value = next;
};

const loadChoices = async () => {
  capaItemRows.value = [];
  cmptDictListRaw.value = [];

  if (!capaGbnId.value || !props.findYear || !props.evalCycleCd) {
    buildFromFallbackOnly();
    return;
  }

  loading.value = true;
  try {
    const params = new URLSearchParams({
      findYear: props.findYear,
      evalCycleCd: props.evalCycleCd,
      capaGbnId: capaGbnId.value,
    });
    if (props.evalGrpId) params.append('evalGrpId', props.evalGrpId);
    if (props.cmptGrpId) params.append('cmptGrpId', props.cmptGrpId);

    const res = await fetchCapaItemAllList(params);
    const list = Array.isArray(res?.capaItemList) ? res.capaItemList : [];
    const dicts = Array.isArray(res?.cmptDictList) ? res.cmptDictList : [];

    let rows = list.map(raw => ({
      capaItemId: String(raw.capaItemId ?? ''),
      capaItemNm: String(raw.capaItemNm ?? ''),
      cmptDictId: String(raw.cmptDictId ?? ''),
      weight: raw.weight != null && raw.weight !== '' ? String(raw.weight) : '',
      checked: false,
    }));

    rows = applyPreselected(rows, props.preselectedItems || []);

    if (!rows.length && (props.fallbackItems || []).length) {
      buildFromFallbackOnly();
      return;
    }

    capaItemRows.value = rows;
    cmptDictListRaw.value = dicts.map(d => ({
      cmptDictId: String(d.cmptDictId ?? ''),
      cmptDictNm: String(d.cmptDictNm ?? ''),
    }));

    initExpandedFromRows(capaItemRows.value);
  } catch {
    buildFromFallbackOnly();
  } finally {
    loading.value = false;
  }
};

/** API 실패 시: 기존 스냅샷만 평면 체크박스 (사전 그룹 없음) */
const buildFromFallbackOnly = () => {
  const fb = props.fallbackItems || [];
  cmptDictListRaw.value =
    fb.length > 0
      ? [{ cmptDictId: '_fb', cmptDictNm: t('hrEvaluation.conf.targets.capaItemDefaultTitle') }]
      : [];
  capaItemRows.value = fb.map(raw => ({
    capaItemId: String(raw.capaItemId ?? ''),
    capaItemNm: String(raw.capaItemNm ?? ''),
    cmptDictId: String(raw.cmptDictId ?? '_fb'),
    weight: raw.weight != null && raw.weight !== '' ? String(raw.weight) : '',
    checked: true,
  }));
  initExpandedFromRows(capaItemRows.value);
};

const toggleAllInDict = (cmptDictId, checked) => {
  capaItemRows.value = capaItemRows.value.map(i =>
    i.cmptDictId === cmptDictId ? { ...i, checked: !!checked } : i,
  );
};

const toggleOneItem = (it, v) => {
  capaItemRows.value = capaItemRows.value.map(i =>
    i.capaItemId === it.capaItemId && i.cmptDictId === it.cmptDictId ? { ...i, checked: !!v } : i,
  );
};

watch(
  () => props.modelValue,
  v => {
    if (!v) return;
    void loadChoices();
  },
);

const close = () => {
  isOpen.value = false;
};

const onConfirm = () => {
  const gid = capaGbnId.value;
  const normalized = capaItemRows.value
    .filter(i => i.checked)
    .map(i => ({
      capaItemId: i.capaItemId,
      capaItemNm: i.capaItemNm,
      capaGbnId: gid,
      cmptDictId: i.cmptDictId,
      weight: i.weight,
    }));
  emit('confirm', normalized);
  isOpen.value = false;
};
</script>

<style scoped lang="scss">
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.step07-capa-item-picker-modal__state {
  padding: 24px;
  text-align: center;
  color: #666;
}

/* 일괄설정 모달 본문 max-height 72vh 대비 약간 큰 고정 높이 */
.step07-capa-item-picker-modal__state--sized {
  box-sizing: border-box;
  min-height: 78vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step07-capa-item-picker-modal__layer {
  box-sizing: border-box;
  height: 78vh;
  max-height: 78vh;
  overflow-x: hidden;
  overflow-y: auto;
}

.step07-capa-item-picker-modal__activity {
  padding: 4px 0;
}

.step07-capa-item-picker-modal__dict {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.step07-capa-item-picker-modal__dict-head {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.step07-capa-item-picker-modal__expand {
  width: 22px;
  height: 22px;
  border: 1px solid #c5cad3;
  border-radius: 2px;
  background: #fff;
  cursor: pointer;
  flex-shrink: 0;
  position: relative;

  &.is-closed::after {
    content: '+';
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    line-height: 1;
    color: #333;
  }

  &.is-open::after {
    content: '−';
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    line-height: 1;
    color: #333;
  }
}

.step07-capa-item-picker-modal__dict-master {
  flex-shrink: 0;
}

.step07-capa-item-picker-modal__dict-nm {
  font-size: 14px;
  margin: 0;
}

.step07-capa-item-picker-modal__dict-body {
  margin-left: 30px;
  padding-left: 8px;
  border-left: 1px solid #e8eaed;
}

.step07-capa-item-picker-modal__tbl {
  width: 100%;
  border-collapse: collapse;
}

.step07-capa-item-picker-modal__cell {
  padding: 6px 4px;
  text-align: left;
  vertical-align: top;

  // Step4 역량항목 체크박스와 동일 방향: 좌측 정렬 + 긴 name 줄바꿈 + 첫 줄 광학 맞춤
  :deep(.ui-checkbox) {
    min-width: 0;
    width: 100%;
    align-items: flex-start;
  }

  // large 체크박스(22px) 크기 유지 — 상단 붙음 대신 첫 줄과 가로로 나란히 보이도록
  :deep(.ui-checkbox__input) {
    flex-shrink: 0;
    margin-top: 0;
  }

  :deep(.ui-checkbox__label) {
    text-align: left;
    min-width: 0;
    line-height: 1.4;
    word-break: break-word;
    // (22px - 14px) / 2 ≈ 4px — name을 체크박스 세로 중앙에 맞춤
    padding-top: 1px;
  }
}
</style>

<style lang="scss">
.step07-capa-item-picker-modal > .ui-popup__content {
  width: min(720px, 94vw) !important;
  max-width: min(720px, 94vw) !important;
}

.step07-capa-item-picker-modal > .ui-popup__body {
  overflow: hidden;
}
</style>
